import axios from 'axios';
import JSONbig from 'json-bigint';
import { router } from '@/router';
import { getToken, setToken, getAppId, getRefreshToken, setRefreshToken } from '@/common/utils/index';
import { useLayoutStore } from '@/store';
import { Dialog } from '@/components/Dialog';
import { API_CONTEXT } from '@/api/config';
// 创建 axios 请求实例
const axiosInstance = axios.create({
    baseURL: '',
    timeout: 30000,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json; charset=utf-8',
        deviceType: '4',
    },
    transformResponse: [
        function (data) {
            if (typeof data === 'string') {
                // 空响应体（如 401 返回空 body）直接返回，避免 JSON 解析抛异常吞掉 response 对象
                if (data.length === 0) {
                    return data;
                }
                try {
                    return JSONbig({ storeAsString: true }).parse(data);
                }
                catch (e) {
                    // 解析失败时返回原始字符串，保证 error.response 对象不丢失
                    return data;
                }
            }
            else {
                return data;
            }
        },
    ],
});
// 创建请求拦截
axiosInstance.interceptors.request.use(config => {
    const token = getToken();
    const appId = getAppId();
    const currentMenuId = useLayoutStore().currentMenuId;
    if (token != null)
        config.headers['Authorization'] = "Bearer " + token;
    if (appId != null && appId !== '')
        config.headers['AppCode'] = appId;
    if (currentMenuId != null)
        config.headers['MenuId'] = currentMenuId;
    return config;
}, error => {
    Promise.reject(error);
});
const loginInvalid = () => {
    setToken(null);
    setRefreshToken(null);
    Dialog.closeAll();
    router.push({ name: 'login' });
};
// Token 刷新状态（防并发：多个 401 请求共享同一个 refresh 过程）
let refreshPromise = null;
async function handleAuthExpired(responseOrError) {
    const appId = getAppId();
    // 第三方嵌入页面不接管 401，由父窗口处理
    if (appId != null) {
        return Promise.reject(new Error('登录已过期'));
    }
    // 并发控制：多个 401 请求共享同一个 refreshPromise
    if (!refreshPromise) {
        refreshPromise = (async () => {
            const rt = getRefreshToken();
            if (!rt)
                throw new Error('No refresh token');
            const res = await axios.post(API_CONTEXT + '/upms/login/refresh', { refreshToken: rt });
            if (!res.data?.success)
                throw new Error(res.data?.errorMessage || 'Token refresh failed');
            setToken(res.data.data.tokenData);
            setRefreshToken(res.data.data.refreshToken);
        })()
            .catch(() => {
            setToken(null);
            setRefreshToken(null);
            loginInvalid();
            throw new Error('登录已过期，请重新登录！');
        })
            .finally(() => {
            refreshPromise = null;
        });
    }
    try {
        await refreshPromise;
        // Refresh 成功，用新 token 重试原请求（请求拦截器会自动附上新 token）
        const config = responseOrError.config || responseOrError;
        return axiosInstance.request(config);
    }
    catch {
        return Promise.reject(new Error('登录已过期，请重新登录！'));
    }
}
// 创建响应拦截
axiosInstance.interceptors.response.use((response) => {
    const { data, status } = response;
    // 如果401响应放行至此，执行此逻辑
    if (status === 401) {
        const appId = getAppId();
        if (appId == null) {
            return handleAuthExpired(response);
        }
        return Promise.reject(new Error('您未登录，或者登录已经超时，请先登录！'));
    }
    if (response.data && response.data.errorCode === 'UNAUTHORIZED_LOGIN') {
        // 401, token失效
        const appId = getAppId();
        if (appId == null) {
            return handleAuthExpired(response);
        }
        else {
            return Promise.reject(new Error(response.data.errorMessage));
        }
    }
    else {
        if (response.headers['refreshedtoken'] != null) {
            setToken(response.headers['refreshedtoken']);
        }
        // 判断请求是否成功
        if (!(response.data instanceof Blob) && !response.data.success) {
            return Promise.reject(new Error(response.data.errorMessage || 'error'));
        }
    }
    // 需要原始响应时（如读取 Content-Disposition 文件名）返回整个 response，而不是只返回 data
    if (response.config?.rawResponse) {
        // 拦截器声明的返回类型是 AxiosPromise（完整响应对象），这里需断言成 AxiosResponse
        return Promise.resolve(response);
    }
    return data;
}, error => {
    if (error && error.response) {
        // 401 尝试 refresh，失败再跳登录页
        if (error.response.status === 401) {
            return handleAuthExpired(error);
        }
        let message = '';
        switch (error.response.status) {
            case 302:
                message = '接口重定向了！';
                break;
            case 400:
                message = '参数不正确！';
                break;
            case 403:
                message = '您没有权限操作！';
                break;
            case 404:
                message = `请求地址出错: ${error.response.config.url}`;
                break;
            case 408:
                message = '请求超时！';
                break;
            case 409:
                message = '系统已存在相同数据！';
                break;
            case 500:
                message = '服务器内部错误！';
                break;
            case 501:
                message = '服务未实现！';
                break;
            case 502:
                message = '网关错误！';
                break;
            case 503:
                message = '服务不可用！';
                break;
            case 504:
                message = '服务暂时无法访问，请稍后再试！';
                break;
            case 505:
                message = 'HTTP 版本不受支持！';
                break;
            default:
                message = '异常问题，请联系管理员！';
                break;
        }
        return Promise.reject(new Error(message));
    }
    // error.response 不存在时的兜底处理
    // 尝试从 XHR 对象获取状态码（代理/CORS 场景下 axios 可能不填充 response）
    const xhrStatus = error?.request?.status;
    if (xhrStatus === 401) {
        return handleAuthExpired(error);
    }
    // XHR 完成但 status=0（通常是取消或网络错误），有 refreshToken 时尝试刷新
    if (error?.request?.readyState === 4 && xhrStatus === 0 && getRefreshToken()) {
        return handleAuthExpired(error);
    }
    // JSON 解析异常（transformResponse 抛错导致 response 丢失），有 refreshToken 时尝试刷新
    if ((error?.message?.startsWith('Unexpected') || error?.message?.includes('JSON') || error?.message?.includes('parse')) && getRefreshToken()) {
        return handleAuthExpired(error);
    }
    const errorMessage = error?.message || '未知的网络请求错误';
    return Promise.reject(new Error(errorMessage));
});
export default axiosInstance;
